// Emoji reach a message as native unicode - emoji-mart hands ChatBoxCompose
// the glyph itself and it is appended to the text - so making them bigger is
// a font-size question, not an image one. Nothing is fetched and no asset is
// needed; the platform emoji font draws them as it already does.
//
// Two sizes, the way Teams and Slack do it: an emoji inside a sentence gets a
// modest bump so it still sits on the line, and a message that is nothing but
// emoji gets its own knob. Both are 1.6em for now - 2.6em, then 2em, read as
// excessive on their own line - so an emoji-only message is currently drawn no
// larger; the branch stays so raising it again is a one-line change here. Both
// in em, because the chat renders in a detached window whose base font is not
// necessarily the opener's, and a px here would be right in one window and
// wrong in the other.

export const INLINE_SIZE = '1.6em';
export const JUMBO_SIZE = '1.6em';

// One emoji, as regex source. Written with \u escapes so this file stays
// ASCII - several of these are lone surrogates, which no file could hold as
// literal text anyway.
//
// A digit keycap is a digit followed by the combining keycap mark, so it has
// to be tried before anything that could match the digit alone.
const KEYCAP = '[0-9#*]\uFE0F?\u20E3';
// U+1F000-U+1FFFF, the pictograph planes, as surrogate pairs. Skin tone
// modifiers live here too, which is why they need no separate case.
const ASTRAL = '[\uD83C-\uD83E][\uDC00-\uDFFF]';
// Copyright, registered and trade mark are letters far more often than they
// are emoji, so they only count when the text explicitly asked for the emoji
// presentation with a variation selector.
const PRESENTED = '[\u00A9\u00AE\u2122]\uFE0F';
const BMP = '[\u203C\u2049\u2139\u2194-\u21AA\u231A\u231B\u2328\u23CF' +
    '\u23E9-\u23FA\u24C2\u25AA\u25AB\u25B6\u25C0\u25FB-\u25FE' +
    '\u2600-\u27BF\u2934\u2935\u2B00-\u2BFF\u3030\u303D\u3297\u3299]';
// A variation selector, a zero-width joiner or a keycap mark binds what
// follows to what came before. Consuming them inside the run is what keeps a
// family or a skin tone in one span: split across two spans the browser stops
// seeing a sequence and draws the pieces.
const TRAILER = '[\uFE0E\uFE0F\u200D\u20E3]';

const ONE = '(?:' + KEYCAP + '|' + ASTRAL + '|' + PRESENTED + '|' + BMP + ')';

// Adjacent emoji share one span. Only ever use this with String.replace,
// which resets lastIndex; a .test on a /g regex would carry it between calls.
const EMOJI_RUN = new RegExp('(?:' + ONE + TRAILER + '*)+', 'g');

// A tag, or the text between tags. The message has already been escaped, so
// the only '<' left are the ones linkify put there.
const TAG_OR_TEXT = /<[^>]*>|[^<]+/g;

const TAGS = /<[^>]*>/g;

// Whitespace does not count either way: it is neither an emoji nor a reason
// to call a message ordinary. An empty message has nothing to enlarge.
export const isEmojiOnly = (text) => {
    if (text.trim() === '') {
        return false;
    }
    return text.replace(EMOJI_RUN, '').trim() === '';
};

const wrapRuns = (text, size) => text.replace(EMOJI_RUN, (run) =>
    '<span style="font-size:' + size + ';line-height:1;">' + run + '</span>');

// Takes the escaped, linkified HTML the message item already builds and
// returns it with its emoji enlarged. Tags are stepped over rather than
// scanned, so an emoji inside an href is left where it is instead of having a
// span opened in the middle of an attribute.
export const decorateEmoji = (html) => {
    if (!html) {
        return html;
    }
    const plain = html.replace(TAGS, '');
    if (plain.replace(EMOJI_RUN, '') === plain) {
        return html;
    }
    const size = isEmojiOnly(plain) ? JUMBO_SIZE : INLINE_SIZE;
    return html.replace(TAG_OR_TEXT, (segment) =>
        segment.charAt(0) === '<' ? segment : wrapRuns(segment, size));
};



// WEBPACK FOOTER //
// ./src/utils/emojiText.js